/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'../../../widgets/livewidget/nls/StringResources',
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./api/ChangeVisTypeActionAPI',
	'../../../widgets/livewidget/visSwitcher/VisChangerFlyoutView',
	'../../../lib/@waca/dashboard-common/dist/api/ContentActionsProviderAPI'
], function(_, stringResources, APIFactory, ChangeVisTypeActionAPI, VisChangerFlyoutView, ContentActionsProviderAPI) {

	return class ChangeVisTypeAction {

		constructor({ features }) {
			if(features) {
				this.dashboard = features.API;
				this.features = features;
				this.transaction = features.Transaction;
				features.ContentActions.registerProvider('changeVisTypeAction', this.getAPI());
			}
		}

		destroy() {
			this.dashboard = null;
			this.api = null;
		}

		getAPI() {
			if (!this.api) {
				this.api = APIFactory.createAPI(this, [ChangeVisTypeActionAPI, ContentActionsProviderAPI]);
			}
			return this.api;
		}

		_supportAction(idList, content) {
			const onlyOneElement = Boolean(idList.length === 1);

			const contentIsLiveWidget = Boolean(content && content.getType() === 'widget.live');

			const inSupportedMode = (this.dashboard.getMode() !== this.dashboard.MODES.EDIT_GROUP);

			const state = content && content.getFeature('state');
			const hasError = Boolean(state && !!state.getError());

			return onlyOneElement && contentIsLiveWidget && inSupportedMode && this._isInteractionEnabled('changeVis') && !hasError;
		}

		getContentActionList(idList) {
			const content = this.dashboard.getCanvas().getContent(idList[0]);
			if (this._supportAction(idList, content)) {
				let visualization = content.getFeature('Visualization');
				let changeVisIcon = this._getVisualizationIcon(visualization);
				let handler;
				let viewOptions = {
					height: 202,
					width: 402,
					content: content,
					dashboard: this.dashboard
				};
				let contribution = {
					name: 'changeVisTypeAction',
					label: stringResources.get('visChange'),
					icon: changeVisIcon.id,
					section: ContentActionsProviderAPI.ACTION_SECTION.SELECTION_DATA,
					order: 10,
					type: 'NextView',
					view: {
						module: 'dashboard-analytics/widgets/livewidget/visSwitcher/VisChangerFlyoutView',
						state: viewOptions
					},
					actions: {
						apply: this._changeVisType.bind(this, content)
					}
				};
				_.extend(contribution, {
					text: visualization.getDefinition().getLabel(),
					className: 'buttonWithText',
					callbacks: {
						registerCallback: (button) => {
							handler = ()=>{
								button.updateState({ text: visualization.getDefinition().getLabel() });
							};
							visualization.on('change:type', handler);
						},
						unregisterCallback: () => {
							visualization.off('change:type', handler);
						}
					}
				});
				return [contribution];
			}

			return [];
		}

		_getVisualizationIcon(visualization) {
			const isSchematic = visualization.getDefinition().getProperty('isSchematic');
			const isCustomVis = visualization.getDefinition().getProperty('isCustomVis');
			const visualizationId = visualization.getDefinition().getId();
			const icons = this.features['Icons'];
			let changeVisIcon;
			if (isSchematic) {
				changeVisIcon = visualizationId === 'com.ibm.vis.schematicsPreview' ?  icons.getIcon('svgFile') : icons.getIcon('schematicVisual');
			} else if (isCustomVis) {
				changeVisIcon = visualizationId === 'visualizationPreview' ?  icons.getIcon('codeFile32') : icons.getIcon('customVisual');
			} else {
				changeVisIcon = icons.getIcon(visualizationId);
			}
			return!changeVisIcon ? icons.getIcon('visualizations-changeVisualization') : changeVisIcon;
		}

		_isInteractionEnabled(name) {
			const config = this.dashboard.getAppConfig('interactions') || {};
			if (config.hasOwnProperty(name) && [false, 'false'].indexOf(config[name]) !== -1) {
				return false;
			}
			return true;
		}

		_trackSetType (content, type, options = {}) {
			if (options.recommendedCategory && this.features.LiveWidgetSegment) {
				const trackInfo = {
					category: options.recommendedCategory,
					visualization: content.getFeature('Visualization'),
					content: content,
					info: {
						visId: type
					}
				};
				this.features.LiveWidgetSegment.track(trackInfo);
			}
		}

		_isUnsupportedVisType(content, type) {
			const visDef = this.features['VisDefinitions'].getByType(type);
			if (visDef && visDef.getProperty('supportsOLAP') === false) {
				const olapContentHelper = content.getFeature('Visualization.olapContentHelper');
				return olapContentHelper && olapContentHelper.containsOlap();
			}
			return false;
		}

		_changeVisType(content, type, options = {}) {
			if (this._isUnsupportedVisType(content,type)){
				this.features['Notification'].setMessage(stringResources.get('visualizationDoesNotSupportOLAP'));
				return;
			}
			const recommender = content.getFeature('Visualization.SmartsRecommender');
			if (recommender){
				this._trackSetType(content, type, options);
				let transactionToken;
				if (!_.isEmpty(options.properties)) {
					transactionToken = this.transaction.startTransaction();
					_.each(options.properties, (value, name) => {
						content.setPropertyValue(name, value, transactionToken);
					});
				}
				return recommender.setType(type, transactionToken).then(() => {
					content.getFeature('Toolbar.internal').refresh();
				}).finally(() => {
					if (transactionToken) {
						this.transaction.endTransaction(transactionToken);
					}
				});
			}
		}

		openDialog(renderOptions, dialogOptions) {
			dialogOptions.actions = {
				apply: this._changeVisType.bind(this, dialogOptions.content)
			};
			if (!this.visChangeView) {
				this.visChangeView = new VisChangerFlyoutView(dialogOptions);
			}

			return this.visChangeView.render(renderOptions);
		}

		removeDialog () {
			if (this.visChangeView) {
				this.visChangeView.remove();
				this.visChangeView = null;
			}
		}
	};
});
